import React, { createContext, useContext, useState, useCallback } from 'react';
import ToastContainer from '../components/Toast';
import ConfirmDialog from '../components/ConfirmDialog';

const PopupContext = createContext(null);

export function usePopup() {
  const ctx = useContext(PopupContext);
  if (!ctx) throw new Error('usePopup must be used within PopupProvider');
  return ctx;
}

export function PopupProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const [confirm, setConfirm] = useState({
    open: false,
    message: '',
    title: 'Confirm',
    confirmLabel: 'Confirm',
    cancelLabel: 'Cancel',
    onConfirm: null,
    onCancel: null,
  });

  const showToast = useCallback((message, type = 'info') => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 4000);
  }, []);

  const showConfirm = useCallback(
    ({ title = 'Confirm', message, confirmLabel = 'Confirm', cancelLabel = 'Cancel', onConfirm, onCancel }) => {
      setConfirm({
        open: true,
        title,
        message,
        confirmLabel,
        cancelLabel,
        onConfirm: onConfirm || (() => {}),
        onCancel: onCancel || (() => {}),
      });
    },
    []
  );

  const closeConfirm = useCallback(() => {
    setConfirm((prev) => ({ ...prev, open: false }));
  }, []);

  const handleConfirm = useCallback(() => {
    confirm.onConfirm?.();
    closeConfirm();
  }, [confirm, closeConfirm]);

  const handleCancel = useCallback(() => {
    confirm.onCancel?.();
    closeConfirm();
  }, [confirm, closeConfirm]);

  return (
    <PopupContext.Provider value={{ showToast, showConfirm }}>
      {children}
      <ToastContainer toasts={toasts} />
      <ConfirmDialog
        open={confirm.open}
        title={confirm.title}
        message={confirm.message}
        confirmLabel={confirm.confirmLabel}
        cancelLabel={confirm.cancelLabel}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </PopupContext.Provider>
  );
}
